/**
 * The open conversation's live state: the transcript, the turn in flight, and
 * what the agent is asking for.
 *
 * **Every conversation keeps its own** (#193). The state lives in a session store
 * (`lib/chatSessions.ts`) keyed by thread, not in this hook: a turn writes only
 * into the session it started in, so it keeps running — with its own approval
 * card, Stop and transcript — while the user opens another thread, another tab
 * or another project. This hook binds the open thread's session to the pane.
 *
 * This is the piece `bimgo-ai-web` spreads across `useChat` and `ChatProvider`,
 * kept to what a turn actually needs. It owns the transcript so that a streamed
 * answer, its reasoning and its tool steps stay attached to the message they
 * belong to rather than living in four parallel arrays that have to be zipped
 * back together at render time.
 *
 * Two rules that are not obvious:
 *
 *   - **The streaming message is a real entry in `messages`,** appended empty
 *     when the turn starts and mutated in place as tokens arrive. Keeping it
 *     outside the list and concatenating at render is what makes a transcript
 *     flicker when a turn ends and the "live" copy is swapped for the "final"
 *     one.
 *   - **Stopping a turn aborts the fetch *and* tells the agent.** Aborting only
 *     the fetch leaves the agent running: still spending credits, still driving
 *     Revit. `abortTurn` is not optional politeness.
 */
import { useCallback, useRef, useSyncExternalStore } from "react";
import {
  EMPTY_SESSION_STATE,
  type ChatSessions,
  newDraftKey,
  type ChatSession,
  type ChatSessionState,
} from "../lib/chatSessions";
import {
  collectMentionedTypes,
  extractMentionMetadata,
  type MentionEnrichmentSource,
  type MentionExtractionResult,
} from "../lib/mentionUtils";
import { InvalidElementIdError } from "../lib/elementId";
import { describeAskError } from "../lib/askError";
import type { HostKind } from "../lib/hostStatus";
import { decisionStep } from "../lib/hitlCard";
import { rememberRefusal, retrySignature, retryThread, type PendingRetry } from "../lib/workflowSections";
import { sentAttachment, type Attachment, type MessageAttachment } from "../lib/attachments";
import {
  abortTurn,
  ask,
  AskError,
  type AgentStep,
  type ContextUsage,
  type InterruptPayload,
  type ResumeDecision,
} from "../lib/chat";

/**
 * What the error card shows. `traceId` is the turn's `X-Trace-Id`, when the
 * failure belonged to a turn at all — shown as `Reference: …`, the key support
 * uses to find that turn in the logs. A local refusal (not signed in) has none.
 */
export interface ChatError {
  text: string;
  traceId: string | null;
}

/** What a workflow section adds to a send (`workflows/`). An ordinary send passes none. */
export interface SendOptions {
  /** Its own request fields, sent beside the message (`AskParams.extraBody`). */
  body?: Record<string, unknown>;
  /** The agent accepted the turn — it answered with the turn's thread. */
  onAccepted?: () => void;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  /** The model's thinking, when it streamed any. Shown behind a disclosure. */
  reasoning?: string;
  /** Tools and skills this turn ran. */
  steps?: AgentStep[];
  /**
   * The files this message was sent with.
   *
   * `MessageAttachment`, not the composer's `Attachment`: by the time a message
   * is in the transcript there is no upload left to be in flight and no `size`
   * worth showing, and history replay reconstructs these from URLs alone.
   */
  attachments?: MessageAttachment[];
  /**
   * How this reply failed, shown where it failed.
   *
   * A turn that dies mid-flight used to report only above the composer, which
   * put the failure at the bottom of the screen with no relation to the step
   * that caused it — with three tool rows above it the reader could not tell
   * which one broke, and a thread reopened later showed the steps with no sign
   * anything had gone wrong at all. `bimgo-ai-web` renders its `error` event in
   * the flow (`Message.tsx`'s `ErrorSection`); this is the same idea.
   *
   * Only failures that belong to a turn land here. A refusal raised before any
   * turn began — not signed in — has no message to attach to and stays on the
   * composer.
   */
  error?: ChatError;
}

/** The plan the user approved, pinned while the agent executes it. */
export interface ActivePlan {
  title: string;
  plan: string;
}

export interface ChatTurn {
  messages: ChatMessage[];
  /** The open conversation's turn is in flight. */
  busy: boolean;
  /** Waiting on the user to approve or refuse an action. */
  interrupt: InterruptPayload | null;
  usage: ContextUsage | null;
  /** A refused request or a terminal `error` event, in the user's words. */
  error: ChatError | null;
  /** Pinned above the composer while the agent works through it. */
  activePlan: ActivePlan | null;
  /**
   * Another running conversation is driving a CAD instance this one would send to
   * (#193). The instance runs one command at a time, so sending now would fail
   * mid-turn; the composer says so instead. Null when nothing is in the way.
   */
  instanceInUse: { kind: HostKind } | null;
  /**
   * This thread ran a turn in this app session, so its transcript here is fresher
   * than its saved history — the pane shows it rather than refetching.
   */
  isLive: (threadId: string) => boolean;
  clearPlan: () => void;
  /** `options` is a workflow section's (`workflows/`); an ordinary send passes none. */
  send: (text: string, attachments?: Attachment[], options?: SendOptions) => void;
  respond: (decision: ResumeDecision) => void;
  /**
   * Continue the open workflow conversation's cut-off run from the agent's saved
   * checkpoint, with no new message (`workflows/continue`). Nothing happens
   * without a thread, while busy, or outside a workflow's conversation.
   */
  continueRun: (options?: { confirmDestructive?: boolean }) => void;
  stop: () => void;
  /** Replace the open transcript — used when a thread's history loads. Ignored mid-turn. */
  reset: (messages: ChatMessage[]) => void;
}

let counter = 0;
/** Ids are for React keys only; nothing persists them. */
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter}`;
}

/** Which vendor of `targets` a running conversation holds — for the composer's sentence. */
function heldKind(holder: ChatSession, targets: readonly string[]): HostKind {
  const held = Object.entries(holder.pinned?.sessions ?? {}) as [HostKind, string][];
  return held.find(([, id]) => targets.includes(id))?.[0] ?? "revit";
}

export function useChatTurn({
  projectId,
  organizationId,
  userId,
  token,
  threadId,
  model,
  agentMode,
  reasoningEffort,
  targetSessionId,
  targetSessions,
  workflowId = null,
  onThreadId,
  onTurnSettled,
  onThreadCreated,
  mentionEnrichment,
  sessions: store,
  newChat = 0,
  t,
}: {
  projectId: string | null;
  organizationId: string | null;
  userId: string | null;
  token: string | null;
  /** The open conversation, or null for a new one. */
  threadId: string | null;
  /** The picked model, or null to let the agent use its plan default. */
  model: { provider: string; model: string } | null;
  agentMode: string;
  /** The reasoning chip (#111); pinned per turn like `agentMode`. Undefined sends nothing. */
  reasoningEffort?: string;
  /**
   * The CAD instance this conversation is bound to, or null for none.
   *
   * Pinned per turn like `agentMode`, for the same reason: a resume must reach
   * the instance the interrupted turn was addressing. Approving a write and
   * having it land in a document the user switched to in the meantime is the
   * failure that argument exists to prevent.
   */
  targetSessionId: string | null;
  /**
   * One id per connected vendor (#5) — the set the agent builds its tool set
   * from. Pinned per turn exactly as `targetSessionId` is, for the same reason.
   */
  targetSessions: Partial<Record<HostKind, string>>;
  /**
   * The workflow this conversation belongs to, or null for chat. Decides the
   * route (`AskRoute`): a workflow's turns go to its own endpoints, never `/ask`.
   * Pinned per turn like the instance, so an approval on a reopened conversation
   * follows the interrupted turn's route.
   */
  workflowId?: string | null;
  /**
   * The agent minted a thread id for the conversation **on screen**. Not called for
   * a new chat the user has since left: that one is re-keyed in the store and found
   * from History, rather than pulling the user back to it (#193).
   */
  onThreadId: (threadId: string) => void;
  /**
   * A turn is over — every exit path, and only once it really is over.
   *
   * The shell reloads the thread list from here, as the reference does from the
   * same place. It has to happen on **failure and abort too**: the thread was
   * created before the first token, so a turn that died still left a
   * conversation the History drawer would otherwise never list.
   */
  onTurnSettled?: () => void;
  /**
   * The agent created a thread for a new chat. It exists — titled — from the first
   * event, so the shell lists it now rather than when the turn ends, which for a
   * turn that stops to ask for approval was only after the approval.
   *
   * It also says which route created it — `workflowId` null for a chat — so the
   * pane knows what the conversation is without asking the agent. Asking was a
   * round trip that hid the answer streaming in behind "Loading..." (and, when the
   * lookup failed, kept it hidden). Called before `onThreadId`, so the answer is
   * recorded before the selection moves to the new thread.
   */
  onThreadCreated?: (thread: { threadId: string; organizationId: string; projectId: string; userId: string; workflowId: string | null }) => void;
  /**
   * The document context's cache, for the send path (item BI): a mention's
   * parameters gain their definition / storage type, a CADLink its import
   * metadata. Null when no instance is bound — the markers still resolve.
   */
  mentionEnrichment?: MentionEnrichmentSource | null;
  /**
   * Every conversation's state — held by the shell, which also lists the running
   * ones in the sidebar and stops them all when it goes (sign-out, org gate).
   */
  sessions: ChatSessions;
  /**
   * Bumped by the shell each time the user asks for a new chat. With no thread open
   * the thread id does not change, so this is what tells "New" apart from staying
   * put — see the draft below.
   */
  newChat?: number;
  /** Passed in rather than read here so the hook stays testable without a provider. */
  t: (key: string, vars?: Record<string, string | number>) => string;
}): ChatTurn {
  useSyncExternalStore(store.subscribe, () => store.version);

  /**
   * The conversation on screen: its thread, or a draft for a new chat.
   *
   * A fresh draft is minted whenever the user asks for a new chat — New, leaving a
   * thread, a project switch — and the draft in hand has been used. Keyed on all
   * three, not the thread alone: a chat sent a moment ago has no thread yet, so
   * "New" changed nothing and reopened the chat still running under its draft key.
   * The running one carries on off screen, is filed under its thread when the agent
   * mints it, and shows in the sidebar's Running list.
   */
  const draftRef = useRef<string>("");
  if (!draftRef.current) draftRef.current = newDraftKey();
  const identity = `${threadId ?? ""}|${newChat}|${projectId ?? ""}`;
  const prevIdentityRef = useRef(identity);
  if (prevIdentityRef.current !== identity) {
    if (threadId === null) {
      const draft = store.peek(draftRef.current);
      if (draft && (draft.ran || draft.state.messages.length > 0)) draftRef.current = newDraftKey();
    }
    prevIdentityRef.current = identity;
  }
  const key = threadId ?? draftRef.current;
  const shownKeyRef = useRef(key);
  shownKeyRef.current = key;
  const shown = store.peek(key);
  const state: ChatSessionState = shown?.state ?? EMPTY_SESSION_STATE;

  const tokenRef = useRef(token);
  tokenRef.current = token;
  const enrichmentRef = useRef<MentionEnrichmentSource | null>(mentionEnrichment ?? null);
  enrichmentRef.current = mentionEnrichment ?? null;
  // Read when a turn reports back, possibly long after the render that started it.
  const onThreadIdRef = useRef(onThreadId);
  onThreadIdRef.current = onThreadId;
  const onTurnSettledRef = useRef(onTurnSettled);
  onTurnSettledRef.current = onTurnSettled;
  const onThreadCreatedRef = useRef(onThreadCreated);
  onThreadCreatedRef.current = onThreadCreated;
  const tRef = useRef(t);
  tRef.current = t;
  const targetsRef = useRef<string[]>([]);
  targetsRef.current = Object.values(targetSessions).filter((id): id is string => typeof id === "string");
  // Read where a turn is asked for — `send`, `respond`, `continueRun` — and handed
  // to `run` from there: `run` itself may start after a wait (the CADLink
  // prefetch), when this names whichever conversation is on screen by then.
  const workflowIdRef = useRef(workflowId);
  workflowIdRef.current = workflowId;

  /**
   * A section send the agent refused **after** creating its thread (the refusal
   * carries `details.threadId`): the next identical send from the same chat goes to
   * that thread instead of making another empty one.
   */
  const pendingRetry = useRef<PendingRetry | null>(null);

  const patchState = useCallback(
    (session: ChatSession, change: Partial<ChatSessionState>) => store.update(session, (s) => ({ ...s, ...change })),
    [store],
  );

  const reset = useCallback(
    (next: ChatMessage[]) => {
      const session = store.session(shownKeyRef.current);
      // History arriving under a running turn would replace what it is writing.
      if (session.state.busy) return;
      store.update(session, (s) => ({ ...s, messages: next, interrupt: null, error: null }));
    },
    [store],
  );

  const run = useCallback(
    (
      session: ChatSession,
      params: {
        /** The workflow of the conversation the turn was asked for in, or null for chat. */
        workflowId: string | null;
        text?: string;
        resume?: ResumeDecision;
        /** Pick a cut-off run up from its checkpoint: no message, the same reply goes on. */
        continue?: { confirmDestructive: boolean };
        attachments?: Attachment[];
        decided?: AgentStep;
        options?: SendOptions;
      },
    ) => {
      // A conversation stays in the project it began in, whatever is selected now.
      const turnProjectId = session.projectId ?? projectId;
      if (!turnProjectId || !organizationId || !userId || !token) {
        patchState(session, { error: { text: tRef.current("chat.signInFirst"), traceId: null } });
        return;
      }
      const turnOrganizationId = organizationId;
      const turnUserId = userId;
      let extraction: MentionExtractionResult;
      try {
        extraction = extractMentionMetadata(
          params.text ?? "",
          enrichmentRef.current ?? undefined,
        );
      } catch (error) {
        if (error instanceof InvalidElementIdError) {
          patchState(session, { error: { text: error.message, traceId: null } });
          return;
        }
        throw error;
      }

      // A resume or a continue carries on the earlier turn, so it keeps that turn's
      // settings when they are still known (a reopened conversation pins afresh).
      const continuing = Boolean(params.resume || params.continue);
      if (!continuing || !session.pinned) {
        session.pinned = {
          mode: agentMode,
          effort: reasoningEffort,
          instance: targetSessionId,
          sessions: targetSessions,
          workflowId: params.workflowId,
        };
      }
      const pinned = session.pinned;
      session.projectId = turnProjectId;
      session.ran = true;

      // Only a section send retries onto a refused turn's thread, and only the
      // same send from the same chat; anything else drops the remembered thread.
      const body = params.options?.body;
      const retrySend = {
        key: session.key,
        signature: retrySignature(params.text, params.attachments ?? [], body),
        hasBody: body !== undefined,
      };
      const retryThreadId = retryThread(pendingRetry.current, retrySend);
      if (!retryThreadId) pendingRetry.current = null;

      const controller = new AbortController();

      // The assistant's reply exists from the first frame, so tokens have
      // somewhere to land and the transcript does not jump when it appears.
      //
      // A resume is the *same* reply going on (item BS): the old web kept one
      // message whose events ran on past the decided card, and a new bubble
      // here put the thread's gap between "approve" and what it approved. So
      // the paused reply is reused — its steps kept, the decision appended as
      // a row, the resumed stream's steps and text added after.
      // A continue is the same again: the cut-off reply picks up where it stopped.
      const paused = continuing
        ? [...session.state.messages].reverse().find((m) => m.role === "assistant")
        : undefined;
      const replyId = paused?.id ?? nextId("a");
      const priorSteps: AgentStep[] = [...(paused?.steps ?? []), ...(paused && params.decided ? [params.decided] : [])];
      let needsBreak = (paused?.text ?? "").trim() !== "";
      // The user's own message carries its attachments, so the transcript shows
      // what the model was actually given rather than the text alone. The bubble
      // is drawn for attachments **without** text too: `send` allows an
      // attachments-only message, and a reply to a question that never appeared
      // reads as the model talking to itself.
      const sent = (params.attachments ?? []).map(sentAttachment);
      store.update(session, (s) => ({
        ...s,
        messages: paused
          ? s.messages.map((m) => (m.id === replyId ? { ...m, steps: priorSteps } : m))
          : [
              ...s.messages,
              ...(params.text || sent.length > 0
                ? [
                    {
                      id: nextId("u"),
                      role: "user" as const,
                      text: params.text ?? "",
                      ...(sent.length > 0 ? { attachments: sent } : {}),
                    },
                  ]
                : []),
              { id: replyId, role: "assistant" as const, text: "" },
            ],
        busy: true,
        error: null,
        interrupt: null,
      }));
      store.setController(session, controller);

      // Every write goes to *this* session, wherever the user is now.
      const patch = (change: (m: ChatMessage) => ChatMessage) =>
        store.update(session, (s) => ({ ...s, messages: s.messages.map((m) => (m.id === replyId ? change(m) : m)) }));

      /** This turn stopped to ask rather than finishing. */
      let pausedForApproval = false;

      // The old web's `useChat`: the `[[mention:...]]` markers become the
      // model's clean text (labels, no `@`) plus a `## Mentioned Revit
      // Elements` block sent as `system_input`. The transcript above keeps the
      // markers — that is what renders as chips. The agent parses nothing.
      void ask(
        {
          userInput: extraction.cleanContent.trim(),
          ...(extraction.systemMessageBlock ? { systemInput: extraction.systemMessageBlock } : {}),
          projectId: turnProjectId,
          organizationId,
          userId,
          threadId: session.threadId ?? retryThreadId,
          // A workflow's conversation posts to its own routes. "Start" is decided by the
          // adopted thread, not the retry id: a start the agent refused after creating
          // its thread is retried as a start, on that thread.
          route: pinned.workflowId
            ? { kind: "workflow", workflowId: pinned.workflowId, start: session.threadId === null }
            : { kind: "chat" },
          accessToken: token,
          agentMode: pinned.mode,
          ...(pinned.effort ? { reasoningEffort: pinned.effort } : {}),
          ...(pinned.instance ? { targetSessionId: pinned.instance } : {}),
          ...(Object.keys(pinned.sessions).length > 0 ? { targetSessions: pinned.sessions } : {}),
          ...(model ? { llmProvider: model.provider, llmModel: model.model } : {}),
          ...(params.attachments?.length ? { attachments: params.attachments } : {}),
          abortSignal: controller.signal,
          ...(params.resume ? { resume: params.resume } : {}),
          ...(params.continue ? { continue: params.continue } : {}),
          ...(body ? { extraBody: body } : {}),
        },
        {
          onThreadInfo: (id) => {
            const wasShown = shownKeyRef.current === session.key;
            const created = session.threadId === null;
            store.adopt(session, id);
            if (created) {
              onThreadCreatedRef.current?.({
                threadId: id,
                organizationId: turnOrganizationId,
                projectId: turnProjectId,
                userId: turnUserId,
                workflowId: pinned.workflowId ?? null,
              });
            }
            // Only the conversation on screen moves the selection. A new chat the
            // user has left is found from History; pulling them back to it —
            // across threads, or into another project — is what this used to do.
            if (wasShown) onThreadIdRef.current(id);
            // Accepted: the remembered thread is used up, and the section hears
            // about it (it clears the composer it had locked, say). Only while its
            // conversation is still the one on screen: the composer is the pane's,
            // and by now it may hold another conversation's unsent draft.
            if (pendingRetry.current?.key === session.key) pendingRetry.current = null;
            if (wasShown) params.options?.onAccepted?.();
          },
          onToken: (content) => {
            // Text after a decision starts its own paragraph, not mid-sentence.
            const glue = needsBreak ? "\n\n" : "";
            needsBreak = false;
            patch((m) => ({ ...m, text: m.text + glue + content }));
          },
          onReasoning: (content) =>
            patch((m) => ({ ...m, reasoning: (m.reasoning ?? "") + content })),
          onSteps: (steps) => patch((m) => ({ ...m, steps: [...priorSteps, ...steps] })),
          // What the model said before a call moves onto that call's row, so
          // the answer below the steps holds only what came after the last one.
          onTextToStep: (moved) =>
            patch((m) => (m.text.endsWith(moved) ? { ...m, text: m.text.slice(0, -moved.length).trimEnd() } : m)),
          // A stopped turn says so in the transcript. The partial answer stays —
          // it was really written — but on its own it reads as the model trailing
          // off, which is the one thing pressing Stop should never look like.
          onAborted: () =>
            store.update(session, (s) => ({
              ...s,
              messages: [
                ...s.messages,
                { id: nextId("s"), role: "system" as const, text: tRef.current("chat.stoppedNotice") },
              ],
            })),
          onInterrupt: (payload) => {
            pausedForApproval = true;
            patchState(session, { interrupt: payload });
          },
          onContextUsage: (usage) => patchState(session, { usage }),
          // A terminal `error` event carries the agent's own code; the
          // catalogue turns it into the sentence that says what to do about it.
          // It lands on the reply, under whatever that reply managed to do
          // first, so the failure sits with the work it interrupted.
          onStreamError: (code, message, traceId, details) =>
            patch((m) => ({
              ...m,
              error: {
                // `details` rides along so a refusal for money says *which*
                // block it hit — see `askError.errorKeyFor`.
                text: describeAskError({ code, message, details }, tRef.current),
                traceId: traceId ?? null,
              },
            })),
        },
      )
        .catch((e: unknown) => {
          // The turn never completed — a transport failure, or a non-2xx from
          // the agent. It belongs to the same reply for the same reason.
          patch((m) => ({
            ...m,
            error:
              e instanceof AskError
                ? { text: describeAskError(e, tRef.current), traceId: e.traceId ?? null }
                : { text: e instanceof Error ? e.message : String(e), traceId: null },
          }));
          // A refusal after the agent created the thread: keep its id for a retry,
          // without adopting it — the chat is still a draft, and its text stays put.
          const remembered = e instanceof AskError ? rememberRefusal(retrySend, e.details) : null;
          if (remembered) pendingRetry.current = remembered;
        })
        .finally(() => {
          // An answer that never arrived leaves an empty bubble behind, and an
          // empty placeholder is just noise.
          //
          // A reply paused for approval is kept even when empty: it hosts the
          // card, and the resume continues in it. **A reply carrying an error is
          // kept for the same kind of reason** — this bubble is the only thing
          // reporting the failure, and dropping it would delete the message the
          // user needs to read.
          store.update(session, (s) => ({
            ...s,
            busy: false,
            messages: s.messages.filter(
              (m) =>
                m.id !== replyId ||
                pausedForApproval ||
                m.error !== undefined ||
                m.text.trim() !== "" ||
                m.steps?.length,
            ),
          }));
          if (session.controller === controller) store.setController(session, null);
          // A turn waiting on an approval has not ended — the stream closes
          // cleanly when the graph pauses, so this `finally` runs while the
          // conversation is still mid-flight. Telling the shell it settled here
          // would reload the thread list under a pending prompt, and the resume
          // reaches this same block again when the turn really does finish.
          if (!pausedForApproval) onTurnSettledRef.current?.();
        });
    },
    [
      store,
      patchState,
      projectId,
      organizationId,
      userId,
      token,
      model,
      agentMode,
      reasoningEffort,
      targetSessionId,
      targetSessions,
    ],
  );

  const send = useCallback(
    (text: string, attachments?: Attachment[], options?: SendOptions) => {
      const trimmed = text.trim();
      const session = store.session(shownKeyRef.current);
      // A message that is only attachments is still a message worth sending.
      if ((!trimmed && !attachments?.length) || session.state.busy) return;
      // The agent refuses this too (INSTANCE_BUSY); checked here so nothing is
      // drawn for a turn that will not run.
      if (store.instanceHolder(targetsRef.current, session)) return;
      // The route is fixed here, with the session, before anything is awaited.
      const params = {
        workflowId: workflowIdRef.current ?? null,
        text: trimmed,
        ...(attachments?.length ? { attachments } : {}),
        ...(options ? { options } : {}),
      };
      // A CADLink's import metadata is not in the marker and its cache is
      // never warmed by the menu, so it is fetched before the synchronous
      // extraction runs (the reference's preheat).
      const enrichment = enrichmentRef.current;
      if (enrichment?.prefetch && collectMentionedTypes(trimmed).has("CADLink")) {
        // Busy for the wait, so a second Enter cannot start a second turn.
        patchState(session, { busy: true });
        void enrichment.prefetch("CADLink").finally(() => {
          patchState(session, { busy: false });
          run(session, params);
        });
        return;
      }
      run(session, params);
    },
    [store, run, patchState],
  );

  const respond = useCallback(
    (decision: ResumeDecision) => {
      // The card belongs to the open conversation's own session, so the resume
      // can only ever continue the thread that asked.
      const session = store.session(shownKeyRef.current);
      if (session.state.busy) return;
      // An approved plan is pinned above the composer: it is the acceptance
      // criteria for everything the agent does next, and inside the transcript
      // it would scroll away behind the execution it is meant to be checked
      // against. Edits win over what was proposed.
      const action = session.state.interrupt?.action_requests?.[0];
      if (decision.approved && action?.name === "submit_plan" && !decision.intent) {
        const plan =
          decision.edited_plan?.trim() ||
          (typeof action.args?.["plan"] === "string" ? (action.args["plan"] as string) : "");
        const title =
          decision.edited_title?.trim() ||
          (typeof action.args?.["title"] === "string" ? (action.args["title"] as string) : "");
        if (plan) patchState(session, { activePlan: { title, plan } });
      }
      patchState(session, { interrupt: null });
      run(session, { workflowId: workflowIdRef.current ?? null, resume: decision, ...(action ? { decided: decisionStep(action, decision) } : {}) });
    },
    [store, run, patchState],
  );

  const continueRun = useCallback(
    (options?: { confirmDestructive?: boolean }) => {
      // The open conversation's own cut-off run; only a workflow's conversation has one.
      const session = store.session(shownKeyRef.current);
      if (session.state.busy || !session.threadId || !workflowIdRef.current) return;
      run(session, { workflowId: workflowIdRef.current, continue: { confirmDestructive: options?.confirmDestructive === true } });
    },
    [store, run],
  );

  const stop = useCallback(() => {
    // The open conversation's own turn. This used to send `/abort` for the thread on
    // screen while aborting whichever fetch was running — two different turns once
    // the user had switched.
    const session = store.peek(shownKeyRef.current);
    if (!session) return;
    session.controller?.abort();
    // Tell the agent too — see the header note.
    if (session.threadId && tokenRef.current) {
      void abortTurn(session.threadId, tokenRef.current).catch(() => undefined);
    }
  }, [store]);

  const clearPlan = useCallback(() => {
    const session = store.peek(shownKeyRef.current);
    if (session) patchState(session, { activePlan: null });
  }, [store, patchState]);

  const isLive = useCallback((id: string) => store.peek(id)?.ran === true, [store]);

  const holder = store.instanceHolder(targetsRef.current, shown);

  return {
    messages: state.messages,
    busy: state.busy,
    interrupt: state.interrupt,
    usage: state.usage,
    error: state.error,
    activePlan: state.activePlan,
    instanceInUse: holder ? { kind: heldKind(holder, targetsRef.current) } : null,
    isLive,
    clearPlan,
    send,
    respond,
    continueRun,
    stop,
    reset,
  };
}
